import { UseCaseStep, TestTarget, ParameterValue } from "../../types/TestTarget";

// ステップから使用されているパラメータを抽出
export const extractParametersFromSteps = (
  steps: UseCaseStep[]
): Set<string> => {
  const params = new Set<string>();
  steps.forEach((step) => {
    // ステップ文字列から{{param}}形式のパラメータを抽出
    const stepParams = step.step.match(/\{\{([^}]+)\}\}/g);
    if (stepParams) {
      stepParams.forEach((p) => {
        const paramName = p.replace(/\{\{|\}\}/g, "");
        params.add(paramName);
      });
    }
    // 期待結果からも抽出
    if (step.expect) {
      const expectParams = step.expect.match(/\{\{([^}]+)\}\}/g);
      if (expectParams) {
        expectParams.forEach((p) => {
          const paramName = p.replace(/\{\{|\}\}/g, "");
          params.add(paramName);
        });
      }
    }

    // action_detailのparametersからも抽出
    if (step.action_detail?.step_action?.parameters) {
      step.action_detail.step_action.parameters.forEach((param) => {
        // parameterのvalueから{{param}}形式のパラメータを抽出
        if (param.value) {
          const actionParams = param.value.match(/\{\{([^}]+)\}\}/g);
          if (actionParams) {
            actionParams.forEach((p) => {
              const paramName = p.replace(/\{\{|\}\}/g, "");
              params.add(paramName);
            });
          }
        }
      });
    }
  });
  return params;
};

// TestTargetのIDから名前を探す
export const findTargetName = (
  targetId: string,
  testTargets: TestTarget[]
): string => {
  const findInTargets = (targets: TestTarget[]): TestTarget | null => {
    for (const target of targets) {
      if (target.id === targetId) return target;
      if (target.children) {
        const found = findInTargets(target.children);
        if (found) return found;
      }
    }
    return null;
  };
  const target = findInTargets(testTargets);
  return target?.name || targetId;
};

// 対象画面配下のItemのみを取得
export const getAvailableItems = (
  testTargets: TestTarget[],
  currentViewId?: string
): Array<{ id: string; name: string; depth: number; viewName: string }> => {
  if (!testTargets.length) {
    return [];
  }

  // parentId関係から対象View配下のItemを取得する関数
  const getItemsUnderView = (
    viewId: string
  ): Array<{ id: string; name: string; depth: number; viewName: string }> => {
    const items: Array<{
      id: string;
      name: string;
      depth: number;
      viewName: string;
    }> = [];

    // 対象のViewを探す
    const targetView = testTargets.find((target) => target.id === viewId);
    if (!targetView) {
      return [];
    }

    // 指定された親ID配下の全ての子要素を再帰的に取得
    const getDescendants = (parentId: string, depth = 0): void => {
      const children = testTargets.filter(
        (target) => target.parentId === parentId
      );

      children.forEach((child) => {
        if (child.type === "item") {
          // 末端のItem（子要素を持たないItem）のみを追加
          const hasChildren = testTargets.some(
            (target) => target.parentId === child.id
          );
          if (!hasChildren) {
            items.push({
              id: child.id,
              name: child.name,
              depth: 0,
              viewName: targetView.name,
            });
          }
        }

        // さらにその子要素を再帰的に処理
        getDescendants(child.id, depth + 1);
      });
    };

    // 対象View配下の全ての子孫を取得
    getDescendants(viewId);

    return items;
  };

  // 全Itemを取得する関数（フォールバック用）
  const getAllItems = (): Array<{
    id: string;
    name: string;
    depth: number;
    viewName: string;
  }> => {
    const items: Array<{
      id: string;
      name: string;
      depth: number;
      viewName: string;
    }> = [];

    // 各Itemについて、末端Item（子要素を持たないItem）のみを追加
    testTargets.forEach((target) => {
      if (target.type === "item") {
        // 末端Itemかどうかチェック
        const hasChildren = testTargets.some((t) => t.parentId === target.id);
        if (!hasChildren) {
          // このItemの最上位のViewを探す
          let viewName = "その他";
          let currentParentId = target.parentId;

          while (currentParentId) {
            const parent = testTargets.find((t) => t.id === currentParentId);
            if (!parent) break;

            if (parent.type === "view") {
              viewName = parent.name;
              break;
            }
            currentParentId = parent.parentId;
          }

          items.push({
            id: target.id,
            name: target.name,
            depth: 0,
            viewName,
          });
        }
      }
    });

    return items;
  };

  // currentViewIdがない場合は全Itemを返す
  if (!currentViewId) {
    return getAllItems();
  }

  // 対象View配下のItemを取得
  const viewItems = getItemsUnderView(currentViewId);

  // 対象View配下にItemがない場合は全Itemを表示
  if (viewItems.length === 0) {
    return getAllItems();
  }

  return viewItems;
};

// パラメータ名の重複をチェックする関数
export const validateParameterName = (
  parameters: ParameterValue[],
  index: number,
  newName: string
): { isValid: boolean; errorMessage?: string } => {
  // 空の名前は許可（必須チェックは別途行う）
  if (!newName || newName.trim() === "") {
    return { isValid: true };
  }

  // 名前を正規化（前後の空白を削除、大文字小文字を区別）
  const normalizedName = newName.trim();

  // 同じインデックス以外で同じ名前が存在するかチェック
  const duplicateIndex = parameters.findIndex(
    (param, i) => i !== index && param.name.trim() === normalizedName
  );

  if (duplicateIndex >= 0) {
    return {
      isValid: false,
      errorMessage: `同名のパラメータ「${normalizedName}」が既に存在します`,
    };
  }

  return { isValid: true };
};
